type EnvPair = {
  modern: keyof ImportMetaEnv
  legacy: keyof ImportMetaEnv
  label: string
}

type FirebaseEnvMap = {
  apiKey: string
  authDomain: string
  databaseURL: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
}

const envPairs: EnvPair[] = [
  {
    modern: 'VITE_FIREBASE_API_KEY',
    legacy: 'REACT_APP_API_KEY',
    label: 'API key',
  },
  {
    modern: 'VITE_FIREBASE_AUTH_DOMAIN',
    legacy: 'REACT_APP_AUTH_DOMAIN',
    label: 'Auth domain',
  },
  {
    modern: 'VITE_FIREBASE_DATABASE_URL',
    legacy: 'REACT_APP_DATABASE_URL',
    label: 'Database URL',
  },
  {
    modern: 'VITE_FIREBASE_PROJECT_ID',
    legacy: 'REACT_APP_PROJECT_ID',
    label: 'Project ID',
  },
  {
    modern: 'VITE_FIREBASE_STORAGE_BUCKET',
    legacy: 'REACT_APP_STORAGE_BUCKET',
    label: 'Storage bucket',
  },
  {
    modern: 'VITE_FIREBASE_MESSAGING_SENDER_ID',
    legacy: 'REACT_APP_MESSAGING_SENDER_ID',
    label: 'Messaging sender ID',
  },
]

export const readEnv = (
  modern: keyof ImportMetaEnv,
  legacy: keyof ImportMetaEnv,
) => {
  const modernValue = import.meta.env[modern]
  const legacyValue = import.meta.env[legacy]

  if (typeof modernValue === 'string' && modernValue.length > 0) {
    return modernValue
  }

  if (typeof legacyValue === 'string' && legacyValue.length > 0) {
    return legacyValue
  }

  return ''
}

const localPublicBaseHosts = new Set([
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  '[::1]',
])

const isLocalPublicBase = (value: string) => {
  try {
    return localPublicBaseHosts.has(new URL(value).hostname)
  } catch {
    return false
  }
}

const normalizePublicBase = (value?: string) => {
  const trimmedValue = value?.trim()

  if (!trimmedValue || trimmedValue === '/') {
    return ''
  }

  if (import.meta.env.PROD && isLocalPublicBase(trimmedValue)) {
    return ''
  }

  return trimmedValue.endsWith('/')
    ? trimmedValue.slice(0, -1)
    : trimmedValue
}

const publicBasePath = normalizePublicBase(
  import.meta.env.VITE_LEGACY_PUBLIC_BASE_PATH || import.meta.env.BASE_URL,
)

export const resolvePublicPath = (assetPath: string) => {
  const normalizedPath = assetPath.startsWith('/')
    ? assetPath
    : `/${assetPath}`

  return `${publicBasePath}${normalizedPath}`
}

const firebaseConfig: FirebaseEnvMap = {
  apiKey: readEnv('VITE_FIREBASE_API_KEY', 'REACT_APP_API_KEY'),
  authDomain: readEnv('VITE_FIREBASE_AUTH_DOMAIN', 'REACT_APP_AUTH_DOMAIN'),
  databaseURL: readEnv(
    'VITE_FIREBASE_DATABASE_URL',
    'REACT_APP_DATABASE_URL',
  ),
  projectId: readEnv('VITE_FIREBASE_PROJECT_ID', 'REACT_APP_PROJECT_ID'),
  storageBucket: readEnv(
    'VITE_FIREBASE_STORAGE_BUCKET',
    'REACT_APP_STORAGE_BUCKET',
  ),
  messagingSenderId: readEnv(
    'VITE_FIREBASE_MESSAGING_SENDER_ID',
    'REACT_APP_MESSAGING_SENDER_ID',
  ),
}

const i18nNamespaces = ['translation', 'form_account', 'book'] as const
const supportedLanguages = [
  'en',
  'en-US',
  'es',
  'vi',
  'vi-VN',
  'ja',
  'ko',
  'zh',
  'fr',
  'de',
  'ru',
] as const

export const runtimeConfig = {
  appTitle: import.meta.env.VITE_APP_TITLE || 'Happy Learners',
  publicBasePath,
  assetRoots: [
    'public/locales',
    'public/images',
    'public/flags',
    'public/audios',
  ],
  featureRoots: [
    'src/app',
    'src/features',
    'src/services',
    'src/shared',
  ],
  compatibilityDecisions: [
    'Use Vite as the permanent host bundler.',
    'Avoid React 19-only APIs in migrated code until legacy library compatibility is proven.',
    'Migrate environment access through shared helpers, not direct process.env reads.',
    'Do not carry generated Webpack bundles into the new runtime.',
  ],
  assets: {
    audios: resolvePublicPath('/audios'),
    flags: resolvePublicPath('/flags'),
    images: resolvePublicPath('/images'),
    locales: resolvePublicPath('/locales'),
  },
  firebase: {
    config: firebaseConfig,
    isConfigured: Object.values(firebaseConfig).every((value) => value.length > 0),
  },
  i18n: {
    defaultLanguage: import.meta.env.VITE_DEFAULT_LOCALE || 'en',
    fallbackLanguage: import.meta.env.VITE_FALLBACK_LOCALE || 'en',
    namespaces: i18nNamespaces,
    supportedLanguages,
  },
  envStatus: envPairs.map((pair) => ({
    label: pair.label,
    modern: pair.modern,
    legacy: pair.legacy,
    value: readEnv(pair.modern, pair.legacy),
  })),
} as const
